import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Waves, CheckCircle2, AlertCircle, Phone } from "lucide-react";
import { Button, Input, Field } from "../../components/ui";
import { forgotUsernameRequestOtp, forgotUsernameVerifyOtp } from "../../api/auth";

export default function ForgotUsername() {
  const [step, setStep] = useState(1); // 1 phone, 2 otp, 3 result
  const [phone, setPhone] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const t = setInterval(() => setExpiresIn((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [expiresIn]);

  async function requestOtp(e) {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await forgotUsernameRequestOtp(phone.trim());
      setMaskedPhone(res.data.phone);
      setExpiresIn(res.data.expiresInSeconds);
      setCooldown(30);
      setStep(2);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.message ||
          "Could not send OTP. Check the number and try again."
      );
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await forgotUsernameVerifyOtp(phone.trim(), otp.trim());
      setUsername(res.data.username);
      setStep(3);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.message ||
          "Incorrect or expired OTP."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-green-50 px-4">
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-emerald-400/20 blur-3xl animate-pulse" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-green-400/20 blur-3xl animate-pulse delay-1000" />
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[30rem] w-[30rem] rounded-full bg-emerald-300/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-[440px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-2xl bg-emerald-400/20 blur-2xl animate-pulse" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-2xl shadow-emerald-500/25 transition-transform hover:scale-110 duration-300">
              <img src="/logo.jpg" alt="Logo" className="h-full w-full object-cover rounded-lg" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Recover Username</h1>
          <p className="mt-2 text-sm text-gray-500">
            We'll text a one-time code to your registered number
          </p>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-white/90 p-8 shadow-2xl shadow-emerald-200/50 backdrop-blur-sm">
          {error && (
            <div className="mb-5 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={requestOtp}>
              <Field 
                label="Registered Mobile Number" 
                required 
                hint="10-digit Indian mobile number"
              >
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    autoFocus
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="numeric"
                    maxLength={10}
                    required
                    className="pl-10 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                  />
                </div>
              </Field>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200" 
                size="lg" 
                loading={loading}
              >
                Send OTP
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp}>
              <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <p className="text-sm text-gray-600">
                  Code sent to <span className="font-semibold text-gray-800">{maskedPhone}</span>
                  {expiresIn > 0 && (
                    <span className="ml-2 text-emerald-600 font-medium">
                      · expires in {formatTime(expiresIn)}
                    </span>
                  )}
                </p>
              </div>

              <Field label="One-time code" required>
                <Input
                  autoFocus
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputMode="numeric"
                  maxLength={8}
                  required
                  className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                />
              </Field>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200" 
                size="lg" 
                loading={loading}
              >
                Verify
              </Button>

              <button
                type="button"
                disabled={cooldown > 0}
                onClick={requestOtp}
                className={`mt-3 w-full text-center text-sm font-medium transition-colors ${
                  cooldown > 0 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-emerald-600 hover:text-emerald-700 hover:underline'
                }`}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </form>
          )}

          {step === 3 && (
            <div className="text-center py-4">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <p className="text-sm text-gray-500">Your username is</p>
              <p className="my-3 text-2xl font-bold text-emerald-600 font-mono bg-emerald-50 px-4 py-2 rounded-lg inline-block border border-emerald-200">
                {username}
              </p>
              <Link to="/login">
                <Button className="mt-4 w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200" size="lg">
                  Continue to Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>

        <Link
          to="/login"
          className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
        >
          <ArrowLeft size={16} /> Back to Sign In
        </Link>
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}